import type {
  MachineAttachmentItemPriceOnlyDto,
  MachineDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto,
  ServiceContractDto
} from '@api/machines'
import {
  ARCHIVED_ITEMS,
  displayOldPrice,
  formatPriceAndCurrency,
  Options,
  ServicesTypes
} from '@common/utils'
import {
  type IconColor,
  type IconVariant,
  MolsonAccordion,
  MolsonBox,
  MolsonGrid,
  MolsonIcon,
  MolsonRadioButton,
  MolsonRadioGroup,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { round } from 'lodash'
import { type ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ArchivedNotification } from '../archived-notification/ArchivedNotification'
import {
  type HandleOptionChangeFunctionProps,
  SERVICE_CONTRACT_TYPES,
  type SectionsErrorsType,
  type ServiceContractType
} from '../types'
import { ServiceContractAutocomplete } from './ServiceContractAutoComplete'

export type ServiceContractProps = {
  expandedServicesSections?: Set<string>
  handleOptionChange: HandleOptionChangeFunctionProps
  machine: MachineDto
  machineQuotation: MachineQuotationItemDto
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  sectionError?: boolean
  selectedServiceContract?: ServiceContractType
  setSectionsErrors?: (val: Partial<SectionsErrorsType>) => void
  setSelectedServiceContract?: (val: ServiceContractType) => void
  translationsPrefix: string
  toggleExpandServicesSection?: (key: string) => void
}

export const ServiceContract = ({
  expandedServicesSections,
  handleOptionChange,
  machine,
  machineQuotation,
  quotationItemOldPrices,
  sectionError,
  selectedServiceContract,
  setSectionsErrors,
  setSelectedServiceContract,
  toggleExpandServicesSection,
  translationsPrefix
}: ServiceContractProps) => {
  const { t } = useTranslation('', {
    keyPrefix: translationsPrefix
  })
  const theme = molsonUseTheme()
  const { quoteId, specId } = useParams()

  const itemKey = `${ARCHIVED_ITEMS.SERVICE_CONTRACT}_${machineQuotation?.serviceContract?.id}`

  const [expanded, setExpanded] = useState(
    expandedServicesSections?.has(ServicesTypes.SERVICE_CONTRACT) ||
      sectionError
  )
  const [selectedServiceContractValue, setSelectedServiceContractValue] =
    useState(machineQuotation?.serviceContract ?? '')
  const [serviceContractOptions, setServiceContractOptions] = useState<
    ServiceContractDto[]
  >([])

  const selectedServiceContractDnp = machineQuotation?.serviceContract?.dnp

  const selectedServiceContractOldDnp =
    quotationItemOldPrices.serviceContract?.dnp ?? undefined

  const displayPriceDifference = displayOldPrice(
    selectedServiceContractDnp,
    selectedServiceContractOldDnp
  )

  const extractServiceContractOptions = (serviceContractType?: string) => {
    if (!machine?.serviceContracts) {
      setServiceContractOptions([])
      return
    }

    const filteredOptions = (machine?.serviceContracts ?? []).filter(
      (serviceContract: ServiceContractDto) =>
        serviceContract.type === serviceContractType &&
        !serviceContract.isArchived
    )
    setServiceContractOptions(filteredOptions)
  }

  const displayPrices = (
    price: number,
    shouldRound = true,
    fractionalDigitsCount = 0
  ) => {
    return (
      <MolsonBox component="span" fontWeight={400} ml={0.5}>
        {formatPriceAndCurrency(
          shouldRound ? round(price, 2) : price,
          fractionalDigitsCount
        )}
      </MolsonBox>
    )
  }

  const isServiceContractArchived = machineQuotation.serviceContract?.isArchived

  const onChangeServiceContract = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedServiceContractValue('')
    const selectedOption = e.target.value as ServiceContractType
    setSelectedServiceContract?.(selectedOption)

    if (selectedOption === SERVICE_CONTRACT_TYPES.NONE) {
      setServiceContractOptions([])
      if (machineQuotation?.serviceContract?.id) {
        handleOptionChange({
          option: Options.REMOVE_SERVICE_CONTRACT,
          optionId: machineQuotation?.serviceContract?.id,
          quotationId: String(quoteId ?? specId)
        })
      }
      setSectionsErrors?.({
        serviceContract: false
      })
      return
    }

    extractServiceContractOptions(selectedOption)
  }

  const handleAddRemoveServiceContract = (newValue: number | null) => {
    if (!newValue && machineQuotation.serviceContract?.id) {
      handleOptionChange({
        option: Options.REMOVE_SERVICE_CONTRACT,
        optionId: machineQuotation?.serviceContract?.id,
        quotationId: String(quoteId ?? specId)
      })
      setSectionsErrors?.({
        serviceContract: true
      })
      return
    }

    if (newValue) {
      handleOptionChange({
        option: Options.ADD_SERVICE_CONTRACT,
        optionId: Number(newValue),
        quotationId: String(quoteId ?? specId)
      })
      setSectionsErrors?.({
        serviceContract: false
      })
    }
  }

  const serviceContractSectionElements = {
    costBreakdown: [
      {
        label: t('pricePerHour'),
        price:
          (machineQuotation?.serviceContract?.dnp ?? 0) /
          (machineQuotation?.serviceContract?.hours ?? 1),
        shouldRound: true,
        fractionalDigitsCount: 2
      },
      {
        label: t('totalCost'),
        price: machineQuotation?.serviceContract?.dnp,
        shouldRound: false
      }
    ],
    installments: [
      { label: t('24MonthTerm'), months: 24 },
      { label: t('36MonthTerm'), months: 36 },
      { label: t('48MonthTerm'), months: 48 },
      { label: t('60MonthTerm'), months: 60 }
    ]
  }

  const getSummaryIcon = () => {
    if (isServiceContractArchived) {
      return { icon: 'ERROR_OUTLINE', color: 'error' }
    }
    if (displayPriceDifference) {
      return { icon: 'WARNING', color: 'warning' }
    }
    return undefined
  }

  const summaryIcon = getSummaryIcon()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const selectedServiceContract =
      selectedServiceContractValue as ServiceContractDto
    extractServiceContractOptions(String(selectedServiceContract?.type))
  }, [machine?.serviceContracts])

  useEffect(() => {
    if (sectionError) {
      setExpanded(true)
    }
  }, [sectionError])

  useEffect(() => {
    setSelectedServiceContractValue(machineQuotation?.serviceContract ?? '')
  }, [machineQuotation?.serviceContract])

  if (!(machine && (quoteId || specId))) {
    return null
  }

  return (
    <MolsonAccordion
      onChange={(_, toggle) => {
        toggleExpandServicesSection?.(ServicesTypes.SERVICE_CONTRACT)
        setExpanded(toggle)
      }}
      summaryIcon={summaryIcon?.icon as IconVariant}
      summaryIconColor={summaryIcon?.color as IconColor}
      withTooltip={{
        description: t('agreementsForMaintenance'),
        title: t('serviceContract')
      }}
      expanded={
        expandedServicesSections?.has(ServicesTypes.SERVICE_CONTRACT) ||
        expanded
      }
      primaryText={t('serviceContract')}
    >
      <MolsonRadioGroup
        sx={{
          width: '100%'
        }}
        onChange={onChangeServiceContract}
        row={true}
        value={selectedServiceContract}
      >
        <MolsonGrid container={true} spacing={2}>
          <MolsonGrid item={true} xs={4}>
            <MolsonRadioButton
              formControlLabelProps={{
                label: t('none')
              }}
              radioBtnProps={{
                value: SERVICE_CONTRACT_TYPES.NONE
              }}
              checked={selectedServiceContract === SERVICE_CONTRACT_TYPES.NONE}
            />
          </MolsonGrid>
          <MolsonGrid item={true} xs={4}>
            <MolsonRadioButton
              checked={
                selectedServiceContract === SERVICE_CONTRACT_TYPES.STANDARD
              }
              formControlLabelProps={{
                label: t('standard')
              }}
              radioBtnProps={{
                value: SERVICE_CONTRACT_TYPES.STANDARD
              }}
            />
          </MolsonGrid>
          <MolsonGrid item={true} xs={4}>
            <MolsonRadioButton
              checked={
                selectedServiceContract === SERVICE_CONTRACT_TYPES.ARDUOUS
              }
              formControlLabelProps={{
                label: t('arduous')
              }}
              radioBtnProps={{
                value: SERVICE_CONTRACT_TYPES.ARDUOUS
              }}
            />
          </MolsonGrid>
        </MolsonGrid>
      </MolsonRadioGroup>

      {selectedServiceContract !== SERVICE_CONTRACT_TYPES.NONE && (
        <MolsonBox sx={{ alignItems: 'center', display: 'flex', mt: 5 }}>
          <ServiceContractAutocomplete
            options={serviceContractOptions}
            selectedOption={selectedServiceContractValue}
            sectionError={sectionError}
            isArchived={machineQuotation?.serviceContract?.isArchived}
            displayPriceDifference={displayPriceDifference}
            placeholder={t('selectServiceContract') ?? ''}
            onChange={serviceContractId => {
              handleAddRemoveServiceContract(serviceContractId)
            }}
          />
          {displayPriceDifference !== undefined &&
            machineQuotation?.serviceContract?.id &&
            !sectionError && (
              <MolsonBox
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 0.5,
                  width: '100%'
                }}
              >
                <MolsonTypography
                  sx={{
                    color: theme.palette.secondary[500],
                    pl: 2,
                    textDecoration: 'line-through'
                  }}
                >
                  {formatPriceAndCurrency(selectedServiceContractOldDnp)}
                </MolsonTypography>

                <MolsonIcon
                  variant={
                    (selectedServiceContractDnp ?? 0) >
                    (selectedServiceContractOldDnp ?? 0)
                      ? 'ARROW_UPWARD'
                      : 'ARROW_DOWNWARD'
                  }
                  iconColor={
                    (selectedServiceContractDnp ?? 0) >
                    (selectedServiceContractOldDnp ?? 0)
                      ? 'success'
                      : 'error'
                  }
                />

                <MolsonTypography
                  color={theme.palette.secondary[600]}
                  variant="h4"
                >
                  {formatPriceAndCurrency(selectedServiceContractDnp)}
                </MolsonTypography>
              </MolsonBox>
            )}
        </MolsonBox>
      )}

      {machineQuotation?.serviceContract?.isArchived && (
        <ArchivedNotification id={itemKey} text={t('serviceUnavailable')} />
      )}

      {sectionError && (
        <MolsonTypography variant="error">
          {t('pleaseSelectServiceContract')}
        </MolsonTypography>
      )}

      {machineQuotation?.serviceContract &&
        !machineQuotation.serviceContract.isArchived && (
          <>
            <MolsonTypography mb={1.75} mt={5} variant="h3">
              {t('monthlyPaymentPlanOption')}
            </MolsonTypography>
            <MolsonBox mb={2.5}>
              {serviceContractSectionElements.costBreakdown.map(item => (
                <MolsonTypography variant="h4" key={item.label}>
                  {item.label}
                  {displayPrices(
                    item.price ?? 0,
                    item.shouldRound,
                    item.fractionalDigitsCount
                  )}
                </MolsonTypography>
              ))}
            </MolsonBox>
            <MolsonBox display="flex" flexDirection="column" gap={2}>
              {serviceContractSectionElements.installments.map(item => (
                <MolsonTypography variant="h4" key={item.label}>
                  {item.label}
                  {displayPrices(
                    (machineQuotation?.serviceContract?.dnp ?? 0) / item.months
                  )}
                </MolsonTypography>
              ))}
            </MolsonBox>
          </>
        )}
    </MolsonAccordion>
  )
}
